import React from "react";
import styles from "./BetTableBaccarat.module.css";

// Helper function to map card code to image path
const getCardImage = (cardCode) => {
  if (!cardCode || cardCode === "1" || cardCode.trim() === "") {
    return "/assets/img/cards/patti_back.png";
  }
  return `/assets/img/cards/${cardCode.trim()}.png`;
};

// Helper function to parse card string and split into Player and Banker cards
const parseCards = (cardString) => {
  if (!cardString) {
    const backCard = "/assets/img/cards/patti_back.png";
    return { player: [backCard, backCard], banker: [backCard, backCard] };
  }
  
  const cards = cardString.split(',').map(c => c.trim());
  const backCard = "/assets/img/cards/patti_back.png";
  
  const playerCards = [
    cards[0] && cards[0] !== "1" ? getCardImage(cards[0]) : backCard,
    cards[1] && cards[1] !== "1" ? getCardImage(cards[1]) : backCard
  ];
  
  const bankerCards = [
    cards[2] && cards[2] !== "1" ? getCardImage(cards[2]) : backCard,
    cards[3] && cards[3] !== "1" ? getCardImage(cards[3]) : backCard
  ];
  
  return { player: playerCards, banker: bankerCards };
};

// Helper to normalize string for matching
const normalizeString = (str) => {
  if (!str) return "";
  return str.replace(/\s+/g, ' ').trim().toLowerCase();
};

// Helper to create all exposure key variations
const createExposureKeys = (selection) => {
  if (!selection) return [];
  const keys = [
    selection,
    selection.toLowerCase(),
    selection.toLowerCase().trim(),
    selection.trim(),
    normalizeString(selection),
  ];
  return [...new Set(keys.filter(k => k))];
};

const BetTableBaccarat = ({ data = [], showScoreBox = false, onBetClick, exposures = {}, gameData = null }) => {
  // Parse card data from gameData
  const cardString = gameData?.data?.data?.card || gameData?.data?.card || gameData?.card || "";
  const { player: playerCards, banker: bankerCards } = parseCards(cardString);

  // Top row: Score options (sid 6-10 from API)
  // Mapping: 6="Score 1-4", 7="Score 5-6", 8="Score 7", 9="Score 8", 10="Score 9"
  const topRowSids = [6, 7, 8, 9, 10];
  const scoreNames = {
    6: "Score 1-4",
    7: "Score 5-6",
    8: "Score 7",
    9: "Score 8",
    10: "Score 9"
  };
  const topRowData = topRowSids.map((sid) => {
    const real = data.find((item) => item.sid === sid);
    return real || { nat: scoreNames[sid] || `Score ${sid - 5}`, b: 0, sid: 100 + sid };
  });

  // Bottom row: Main bets in order: "Player Pair", "Player", "Tie", "Banker", "Banker Pair"
  const bottomRowSids = [4, 1, 3, 2, 5];
  const bottomRowData = bottomRowSids.map((sid) => {
    const real = data.find((item) => item.sid === sid);
    const fallbackNames = {
      1: "Player",
      2: "Banker",
      3: "Tie",
      4: "Player Pair",
      5: "Banker Pair"
    };
    return real || { nat: fallbackNames[sid] || `Bet ${sid}`, b: undefined, sid: 200 + sid };
  });

  // Get exposure value for a bet
  const getExposure = (betName) => {
    if (!betName) return 0;
    const keys = createExposureKeys(betName);
    for (const key of keys) {
      if (exposures[key] !== undefined && exposures[key] !== null) {
        const expValue = parseFloat(exposures[key]);
        if (!isNaN(expValue) && expValue !== 0) {
          return expValue;
        }
      }
    }
    return 0;
  };

  // Get min/max from first item
  const minValue = data[0]?.min || "100";
  const maxValue = data[0]?.max || "100K";

  return (
    <div className="casino-container baccarat">
      <div className="baccarat-container">
        {/* Top Row - Scores */}
        <div className="row row5">
          <div className="col-12">
            <div className="row row5">
              {topRowData.map((bet) => {
                const isLocked = bet.b === 0 || bet.b === undefined || bet.gstatus === "SUSPENDED";
                const exposure = getExposure(bet.nat);
                const displayExposure = exposure !== 0 ? (exposure < 0 ? exposure.toFixed(0) : `+${exposure.toFixed(0)}`) : "0";
                const oddsValue = bet.b || 0;
                
                return (
                  <div key={bet.sid} className="col">
                    <div className={`bet-odds ${isLocked ? 'suspended' : ''}`}>
                      {bet.nat} {oddsValue}:1
                    </div>
                    <div className="book" style={{ color: 'black' }}>
                      {displayExposure}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Bottom Row - Main Bets */}
        <div className="row row5 mt-1">
          <div className="col-12">
            <div className="bet-container">
              {bottomRowData.map((bet) => {
                const isLocked = bet.b === 0 || bet.b === undefined || bet.gstatus === "SUSPENDED";
                const exposure = getExposure(bet.nat);
                const displayExposure = exposure !== 0 ? (exposure < 0 ? exposure.toFixed(0) : `+${exposure.toFixed(0)}`) : "0";
                const oddsValue = bet.b || 0;
                const hasCards = bet.nat === "Player" || bet.nat === "Banker";
                const betClass = bet.nat.replace(/\s/g, "-").toLowerCase();
                
                return (
                  <div key={bet.sid} className={`${betClass} ${isLocked ? 'suspended' : ''}`}>
                    <div>
                      {bet.nat === "Player Pair" || bet.nat === "Banker Pair" ? (
                        <>
                          {bet.nat}<br />{oddsValue}:1
                        </>
                      ) : bet.nat === "Tie" ? (
                        <>
                          <div><b className="text-uppercase">{bet.nat}</b></div>
                          <div className="mt-2">{oddsValue}:1</div>
                        </>
                      ) : (
                        <>
                          <b className="text-uppercase">{bet.nat}</b>
                          <span className="d-block">{oddsValue}:1</span>
                          {hasCards && (
                            <div className={bet.nat === "Player" ? "player-card" : "player-card second"}>
                              {(bet.nat === "Player" ? playerCards : bankerCards).map((cardSrc, idx) => (
                                <img 
                                  key={idx} 
                                  src={cardSrc} 
                                  alt={`${bet.nat.toLowerCase()} card ${idx + 1}`}
                                  onError={(e) => {
                                    const cardName = cardSrc.split('/').pop();
                                    const fallbackPath = `/assets/img/cards/${cardName}`;
                                    if (e.target.src !== fallbackPath) {
                                      e.target.src = fallbackPath;
                                    } else {
                                      e.target.style.display = 'none';
                                    }
                                  }}
                                />
                              ))}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                    <div className="book" style={{ color: 'black' }}>
                      {displayExposure}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Min/Max Row */}
        <div className="row row5 mt-1">
          <div className="col-12 text-right">
            <span className="text-dark">
              Min: <span>{minValue}</span> Max: <span>{maxValue}</span>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BetTableBaccarat;

